"use client";

import { useEffect, useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import ActionTable from "@/components/action-table";
import Pagination from "@/components/pagination";
import { Button, Checkbox, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, Textarea } from "@/components/ui";
import {
  fetchActionsBySystemId,
  type ActionRecord,
  type GeneratedAction,
} from "@/services/action-service";
import { getFlowState, updateFlowState } from "@/lib/connector-flow-state";
import { domId } from "@/lib/dom-id";
import { InlineStatus } from "@/components/ui/inline-status";

type AddConnectorReviewStepProps = {
  systemId: string;
  onContinue: () => void;
  onBack: () => void;
  onRequestClose: () => void;
};

export default function AddConnectorReviewStep({
  systemId,
  onContinue,
  onBack,
  onRequestClose,
}: AddConnectorReviewStepProps) {
  const t = useTranslations("ConnectorAddApiReview");
  const pagePurpose = "add-connector-modal-review";
  const [previewActions, setPreviewActions] = useState<GeneratedAction[]>([]);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortBy, setSortBy] = useState<"name" | "module">("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");
  const [editingAction, setEditingAction] = useState<ActionRecord | null>(null);
  const [deletingAction, setDeletingAction] = useState<ActionRecord | null>(null);

  useEffect(() => {
    const flowState = getFlowState();
    if (flowState.previewActions && flowState.previewActions.length > 0) {
      setPreviewActions(flowState.previewActions as GeneratedAction[]);
    }
  }, []);

  const actionsForTable: ActionRecord[] = useMemo(
    () =>
      previewActions.map((action, index) => ({
        ...action,
        action_id: String(index + 1),
        system_id: systemId,
      })),
    [previewActions, systemId],
  );

  const sortedActions = useMemo(() => {
    return [...actionsForTable].sort((left, right) => {
      const leftValue =
        sortBy === "module"
          ? (left.category ?? "").toLowerCase()
          : (left.name ?? "").toLowerCase();
      const rightValue =
        sortBy === "module"
          ? (right.category ?? "").toLowerCase()
          : (right.name ?? "").toLowerCase();

      const compareResult = leftValue.localeCompare(rightValue);
      return sortOrder === "asc" ? compareResult : -compareResult;
    });
  }, [actionsForTable, sortBy, sortOrder]);

  const totalActions = sortedActions.length;
  const paginatedActions = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return sortedActions.slice(startIndex, startIndex + pageSize);
  }, [page, pageSize, sortedActions]);

  const handleSort = (field: string) => {
    if (field !== "name" && field !== "module") {
      return;
    }

    setPage(1);
    setSortOrder((currentOrder) =>
      sortBy === field ? (currentOrder === "asc" ? "desc" : "asc") : "asc",
    );
    setSortBy(field);
  };

  const handleAuthenticationClick = async () => {
    if (!systemId || previewActions.length === 0) return;

    const dontShowPref = localStorage.getItem(
      "ait_confirmActionUpdate_dontShow",
    );
    if (dontShowPref === "true") {
      onContinue();
      return;
    }

    try {
      const existingActions = await fetchActionsBySystemId(systemId);
      if (existingActions.length > 0) {
        setShowConfirmModal(true);
      } else {
        onContinue();
      }
    } catch (err) {
      console.error("Failed to check existing actions:", err);
      setShowConfirmModal(true);
    }
  };

  const handleConfirmSave = () => {
    if (!systemId || previewActions.length === 0) return;

    setShowConfirmModal(false);

    if (dontShowAgain) {
      localStorage.setItem("ait_confirmActionUpdate_dontShow", "true");
    }

    setSaving(true);
    setSaveError(null);

    updateFlowState({
      previewActions,
      hasProcessedApi: true,
    });
    onContinue();
    setSaving(false);
  };

  const handleDelete = (action: ActionRecord) => {
    setDeletingAction(action);
  };

  const confirmDelete = () => {
    if (!deletingAction) return;

    const indexToDelete = parseInt(deletingAction.action_id, 10) - 1;
    const updatedActions = previewActions.filter(
      (_, index) => index !== indexToDelete,
    );
    setPreviewActions(updatedActions);
    updateFlowState({ previewActions: updatedActions });
    setDeletingAction(null);
  };

  const saveEditedAction = (updatedAction: ActionRecord) => {
    const indexToUpdate = parseInt(updatedAction.action_id, 10) - 1;
    const updatedActions = previewActions.map((action, index) => {
      if (index === indexToUpdate) {
        return {
          name: updatedAction.name,
          description: updatedAction.description,
          category: updatedAction.category || "",
          tags: updatedAction.tags || [],
          logic: updatedAction.logic,
          input_parameters: updatedAction.input_parameters || [],
          output_parameters: updatedAction.output_parameters || [],
        };
      }
      return action;
    });
    setPreviewActions(updatedActions);
    updateFlowState({ previewActions: updatedActions });
    setEditingAction(null);
  };

  return (
    <div className="mx-auto mt-8 flex min-h-0 w-full max-w-[850px] flex-1 flex-col">
      {/* The dialog never scrolls: this step takes the dialog's remaining
          height, and the actions table, its last item, takes what is left under
          the summary and scrolls on its own. Only a window too short for the
          summary and the table's 80px minimum makes this body scroll as well. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto pr-1">
        <div className="mb-6 shrink-0">
          <div
            className="rounded-lg border border-slate-200"
            style={{
              background: "linear-gradient(to bottom, #EAA2EC26, #A9BEF426)",
            }}
          >
            <div className="p-4">
              <div className="mb-4 flex items-center gap-2">
                <div
                  className="flex h-6 w-6 items-center justify-center rounded-lg"
                  style={{ backgroundColor: "#f5f1faff" }}
                >
                  <svg
                    className="h-4 w-4"
                    style={{ color: "#713D98" }}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                    />
                  </svg>
                </div>
                <h3 className="bg-gradient-to-r from-[#713D98] to-[#3E3D98] bg-clip-text text-base font-semibold text-transparent">
                  {t("aiSummary.title")}
                </h3>
              </div>

              <div className="grid grid-cols-2 gap-2">
                {[
                  t("aiSummary.items.addedMissingDescriptions"),
                  t("aiSummary.items.normalizedEndpoints"),
                  t("aiSummary.items.autoFilledSchemas"),
                  t("aiSummary.items.enhancedErrorDocs"),
                ].map((item) => (
                  <div
                    key={item}
                    className="rounded-lg border border-slate-100 bg-white p-3"
                  >
                    <div className="flex items-start gap-2">
                      <svg
                        className="mt-0.5 h-5 w-5 shrink-0 text-green-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <span className="text-sm text-slate-700">{item}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <h3 className="mb-2 shrink-0 text-lg font-semibold text-slate-900">
          {t("review.title")}
        </h3>
        <p className="mb-6 shrink-0 text-sm text-slate-600">{t("review.subtitle")}</p>

        <ActionTable
          actions={paginatedActions}
          loading={false}
          onView={() => {}}
          onEdit={setEditingAction}
          onDelete={handleDelete}
          formatDate={() => "-"}
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSort={handleSort}
          hideColumns={["lastUpdated", "actions"]}
          fillHeight
        />
      </div>

      <div className="shrink-0 border-t border-slate-200 bg-white pt-5">
        {totalActions > 0 && (
          <div className="pb-5">
            <Pagination
              purpose="add-connector-modal-review-pagination"
              page={page}
              pageSize={pageSize}
              total={totalActions}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              pageSizeOptions={[5, 10, 20, 50, 100]}
            />
          </div>
        )}

        {saveError && (
          <InlineStatus
            purpose={`${pagePurpose}-save-error`}
            status="error"
            className="mb-5"
            onDismiss={() => setSaveError(null)}
          >
            {saveError}
          </InlineStatus>
        )}

        <div className="flex justify-end gap-3">
          <Button
            data-testid={domId("btn", `${pagePurpose}-back`)}
            onClick={onBack}
            disabled={saving}
            className="mr-auto rounded-md border border-slate-300 bg-white px-6 py-2 text-sm font-medium text-slate-700 disabled:opacity-50"
          >
            {t("actions.back")}
          </Button>
          <Button
            data-testid={domId("btn", `${pagePurpose}-cancel`)}
            onClick={onRequestClose}
            disabled={saving}
            className="rounded-md border border-slate-300 bg-white px-6 py-2 text-sm font-medium text-slate-700 disabled:opacity-50"
          >
            {t("actions.cancel")}
          </Button>
          <Button
            data-testid={domId("btn", `${pagePurpose}-authentication`)}
            onClick={handleAuthenticationClick}
            disabled={saving || previewActions.length === 0}
            variant="unstyled"
            className="rounded-md px-6 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500 disabled:opacity-50"
            style={{
              background:
                previewActions.length > 0 && !saving
                  ? "linear-gradient(to right, #713D98, #3E3D98)"
                  : undefined,
            }}
          >
            {t("actions.authentication")}
          </Button>
        </div>
      </div>

      {showConfirmModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/20 backdrop-blur-sm">
          <div className="mx-4 w-full max-w-md rounded-lg bg-white shadow-xl">
            <div className="p-6">
              <h3 className="mb-4 text-lg font-semibold text-slate-900">
                {t("confirmModal.title")}
              </h3>
              <p className="mb-4 text-sm text-slate-600">
                {t("confirmModal.aboutToSavePrefix")}{" "}
                <span className="font-semibold">
                  {t("confirmModal.actionCount", {
                    count: previewActions.length,
                  })}
                </span>
                {t("confirmModal.aboutToSaveSuffix")}
              </p>
              <ul className="mb-4 list-inside list-disc space-y-2 text-sm text-slate-600">
                <li>
                  <strong>{t("confirmModal.bullets.updateLabel")}</strong>{" "}
                  {t("confirmModal.bullets.updateText")}
                </li>
                <li>
                  <strong>{t("confirmModal.bullets.createLabel")}</strong>{" "}
                  {t("confirmModal.bullets.createText")}
                </li>
              </ul>
              <label
                className="mb-6 flex cursor-pointer items-center gap-2"
                htmlFor={domId("chk", `${pagePurpose}-confirm-dont-show-again`)}
              >
                <Checkbox
                  id={domId("chk", `${pagePurpose}-confirm-dont-show-again`)}
                  checked={dontShowAgain}
                  onCheckedChange={(checked) => setDontShowAgain(checked === true)}
                  className="h-4 w-4 rounded border-slate-300 text-blue-600"
                />
                <span className="text-sm text-slate-700">
                  {t("confirmModal.dontShowAgain")}
                </span>
              </label>
              <div className="flex justify-end gap-3">
                <Button
                  onClick={() => setShowConfirmModal(false)}
                  className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700"
                >
                  {t("actions.cancel")}
                </Button>
                <Button
                  onClick={handleConfirmSave}
                  variant="unstyled"
                  className="rounded-md bg-blue-600 px-6 py-2 text-sm font-medium text-white"
                >
                  {t("confirmModal.confirmContinue")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deletingAction && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/20 backdrop-blur-sm">
          <div className="mx-4 w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="mb-2 text-lg font-semibold text-slate-900">
              {t("deleteModal.title")}
            </h3>
            <p className="mb-4 text-sm text-slate-600">
              {t("deleteModal.messagePrefix")}{" "}
              <strong>{deletingAction.name}</strong>{" "}
              {t("deleteModal.messageSuffix")}
            </p>
            <div className="flex justify-end gap-3">
              <Button
                onClick={() => setDeletingAction(null)}
                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700"
              >
                {t("actions.cancel")}
              </Button>
              <Button
                onClick={confirmDelete}
                variant="unstyled"
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white"
              >
                {t("actions.delete")}
              </Button>
            </div>
          </div>
        </div>
      )}

      {editingAction && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/20 backdrop-blur-sm">
          <div className="mx-4 max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl bg-white shadow-xl">
            <div className="p-6">
              <h3 className="mb-4 text-lg font-semibold text-slate-900">
                {t("editModal.title")}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    {t("editModal.fields.name")}
                  </label>
                  <Input
                    type="text"
                    value={editingAction.name}
                    onChange={(e) =>
                      setEditingAction({ ...editingAction, name: e.target.value })
                    }
                    className="w-full rounded-lg border border-slate-300 px-3 py-2"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    {t("editModal.fields.description")}
                  </label>
                  <Textarea
                    value={editingAction.description}
                    onChange={(e) =>
                      setEditingAction({
                        ...editingAction,
                        description: e.target.value,
                      })
                    }
                    rows={3}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">
                    {t("editModal.fields.category")}
                  </label>
                  <Input
                    type="text"
                    value={editingAction.category || ""}
                    onChange={(e) =>
                      setEditingAction({
                        ...editingAction,
                        category: e.target.value,
                      })
                    }
                    className="w-full rounded-lg border border-slate-300 px-3 py-2"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      {t("editModal.fields.method")}
                    </label>
                    <Select
                      value={editingAction.logic?.api_call?.method || "GET"}
                      onValueChange={(value) =>
                        setEditingAction({
                          ...editingAction,
                          logic: {
                            ...editingAction.logic,
                            api_call: {
                              ...editingAction.logic?.api_call,
                              method: value,
                              endpoint:
                                editingAction.logic?.api_call?.endpoint || "",
                              base_url:
                                editingAction.logic?.api_call?.base_url || "",
                            },
                          },
                        })
                      }
                    >
                      <SelectTrigger className="w-full rounded-lg border border-slate-300 px-3 py-2">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="GET">{t("methods.get")}</SelectItem>
                        <SelectItem value="POST">{t("methods.post")}</SelectItem>
                        <SelectItem value="PUT">{t("methods.put")}</SelectItem>
                        <SelectItem value="DELETE">{t("methods.delete")}</SelectItem>
                        <SelectItem value="PATCH">{t("methods.patch")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <label className="mb-1 block text-sm font-medium text-slate-700">
                      {t("editModal.fields.endpoint")}
                    </label>
                    <Input
                      type="text"
                      value={editingAction.logic?.api_call?.endpoint || ""}
                      onChange={(e) =>
                        setEditingAction({
                          ...editingAction,
                          logic: {
                            ...editingAction.logic,
                            api_call: {
                              ...editingAction.logic?.api_call,
                              endpoint: e.target.value,
                              method:
                                editingAction.logic?.api_call?.method || "GET",
                              base_url:
                                editingAction.logic?.api_call?.base_url || "",
                            },
                          },
                        })
                      }
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 font-mono text-sm"
                    />
                  </div>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3 border-t border-slate-200 pt-4">
                <Button
                  onClick={() => setEditingAction(null)}
                  className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700"
                >
                  {t("actions.cancel")}
                </Button>
                <Button
                  onClick={() => saveEditedAction(editingAction)}
                  className="rounded-lg px-4 py-2 text-sm font-medium text-white"
                  style={{
                    background: "linear-gradient(to right, #713D98, #3E3D98)",
                  }}
                >
                  {t("actions.save")}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
